// Dialog de troca de marca — compartilhado entre a sidebar (desktop) e o drawer (mobile).
// Diretor (da company ou de plataforma) enxerga TODAS as marcas da company via GET /v1/brands; clicar numa troca na hora e,
// se ele ainda não tiver acesso, cria o membership antes de ativar. Para os demais
// papéis, a consulta cross-org nem é feita: a lista vem apenas das filiações do usuário.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppCache } from "@/hooks/use-app-cache";
import { toast } from "sonner";
import {
  Building2,
  Check,
  Plus,
  RotateCcw,
  Trash2,
  Network,
  ArrowUpRight,
  Search,
} from "@organizacaox/lai-design-system/icons";
import { invalidateAppAccess, useActiveOrganization } from "@/lib/auth-client";
import { BrandDialogSkeleton } from "@/components/screen-skeletons";
import { Skeleton } from "@organizacaox/lai-design-system";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@organizacaox/lai-design-system";
import { ScrollArea } from "@organizacaox/lai-design-system";
import { Badge } from "@organizacaox/lai-design-system";
import { Spinner } from "@organizacaox/lai-design-system";
import { Button } from "@organizacaox/lai-design-system";
import { Input } from "@organizacaox/lai-design-system";
import { Label } from "@organizacaox/lai-design-system";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@organizacaox/lai-design-system";
import type { BrandSummary } from "@/api/brands";
import { BrandCreateDialog } from "@/brand-create-dialog";
import {
  useAllBrands,
  useBrandQueueCounts,
  useCreateBrand,
  useMyBrands,
  usePurgeBrand,
  useReactivateBrand,
  useSwitchToBrand,
} from "@/hooks/use-brands";
import { useCanCreateBrand, useCompanyBrands } from "@/hooks/use-viewer-role";
import { Link } from "@organizacaox/lai-design-system/router";
import { useAccessMe } from "@/hooks/use-roles";
import { platformSearchScore } from "./platform-search-model.js";
import { formatNumber } from "./lib/locale-format.js";
import { cn } from "@/lib/utils";
import "./i18n.js";

export function BrandDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const { invalidate: invalidateCache } = useAppCache();
  // Quem vê todas as marcas da company é o diretor dela (director em alguma
  // marca) ou o de plataforma, num cliente do tipo 'grupo'. Para o cliente
  // 'unica' a listagem não é só negada pelo back (403): ela não teria o que
  // listar além da única marca dele, que já vem por /brands/mine. Marca
  // desativada (reativar/apagar) só volta para a plataforma — o back filtra.
  const companyBrands = useCompanyBrands();
  const canCreateBrand = useCanCreateBrand();
  const { data: access, isPending: accessPending, isError: accessError } = useAccessMe();
  const canOfferFranchise = !accessPending && !accessError && access?.companyType === "unica";
  const { data: activeOrg, refetch: refetchActiveOrg } = useActiveOrganization();
  const { data: memberBrands, isLoading: isLoadingMemberBrands } = useMyBrands({ enabled: open });
  const { data: allBrands, isLoading: isLoadingAllBrands } = useAllBrands({
    enabled: open && companyBrands,
  });
  // Tamanho da fila de cada marca, para escolher ANTES de entrar. Chega à parte
  // da lista: a lista abre na hora e os números entram quando vierem; se a
  // contagem falhar, a linha só fica sem número.
  const { data: queueCounts, isLoading: isLoadingQueueCounts } = useBrandQueueCounts({
    enabled: open,
  });
  const { mutateAsync: reactivate } = useReactivateBrand();
  const { mutateAsync: purge } = usePurgeBrand();
  const { mutateAsync: create } = useCreateBrand();
  const { mutateAsync: switchBrand } = useSwitchToBrand();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busca, setBusca] = useState("");
  // Marca em vias de ser apagada DE VEZ, e o nome digitado para confirmar. O
  // texto é a trava: a ação não tem desfazer, então não pode caber num clique.
  const [apagando, setApagando] = useState<BrandSummary | null>(null);
  const [confirmacao, setConfirmacao] = useState("");
  const confirmado = apagando !== null && confirmacao.trim() === apagando.name;

  // Marca desativada (DELETE /v1/brands/:id) só chega aqui na listagem cross-org
  // do diretor de plataforma: /brands/mine já não a devolve. Ela não é
  // selecionável — o join recusaria com 409 —, mas é daqui que sai a reversão,
  // porque depois da desativação não existe mais um diretor da marca para pedi-la.
  async function handleReactivate(brand: BrandSummary) {
    if (busyId) return;
    setBusyId(brand.id);
    try {
      // O hook já reativa e derruba o cache inteiro (ver use-brands.ts).
      await reactivate(brand.id);
      toast.success(t("brandDialog.reactivated", { name: brand.name }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.reactivateError"));
    } finally {
      setBusyId(null);
    }
  }

  // Exclusão DEFINITIVA, disponível só na linha da marca já excluída: a
  // desativação é a rede de segurança, e o back recusa marca no ar com 409.
  // Aqui a marca some do banco — leads, ligações, gravações, filas e chaves —
  // e não há reversão, ao contrário do handleReactivate acima.
  async function handlePurge(brand: BrandSummary) {
    if (busyId) return;
    setBusyId(brand.id);
    try {
      const out = await purge(brand.id);
      setApagando(null);
      setConfirmacao("");
      toast.success(t("brandDialog.purged", { name: brand.name }), {
        description: t("brandDialog.purgedDescription", { count: out.total }),
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.purgeError"));
    } finally {
      setBusyId(null);
    }
  }

  // Defesa em profundidade: mesmo uma resposta cross-org inesperada nunca é
  // usada por quem não tem acesso cross-marca.
  const canSeeAllBrands = companyBrands && Array.isArray(allBrands);
  const brands = canSeeAllBrands ? allBrands : (memberBrands ?? []);
  const isLoading = isLoadingMemberBrands || (companyBrands && isLoadingAllBrands);
  const activeId = activeOrg?.id ?? "";
  // Mesmo critério da busca da plataforma: ignora caixa e acentos e aceita
  // palavras fora de ordem ("centro loja" acha "Loja Centro").
  const visibleBrands = busca.trim()
    ? brands.filter((brand) => platformSearchScore(brand.name, busca) > 0)
    : brands;

  // Chamado pelo BrandCreateDialog com o nome já validado. O erro é engolido
  // aqui de propósito: vira toast e o dialog de criação segue aberto com o que
  // foi digitado, em vez de fechar e perder o texto.
  async function handleCreate(name: string) {
    if (busyId) return;

    setBusyId("create-brand");
    try {
      const brand = await create(name);
      // O back já grava o membership. Definir a nova marca como ativa deixa o
      // fluxo pronto para o usuário começar a configurá-la imediatamente.
      await switchBrand(brand);
      invalidateAppAccess();
      await invalidateCache();
      await refetchActiveOrg();
      setCreating(false);
      toast.success(t("brandDialog.created", { name: brand.name }));
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.createError"));
    } finally {
      setBusyId(null);
    }
  }

  async function handleSelect(brand: BrandSummary) {
    if (busyId) return;
    if (brand.id === activeId) {
      onOpenChange(false);
      return;
    }
    setBusyId(brand.id);
    try {
      await switchBrand(brand);
      invalidateAppAccess();
      // A UI usa somente a organização ativa e as listas escopadas do nosso
      // /v1/brands. Não consulta a listagem genérica do Better Auth, que não
      // conhece a fronteira de company.
      await refetchActiveOrg();
      // Novo acesso muda isMember; dados servidos por-org precisam recarregar.
      await invalidateCache();
      toast.success(t("brandDialog.activeToast", { name: brand.name }));
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.switchError"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(aberto) => {
        // A busca não sobrevive ao fechamento: reabrir mostra a lista inteira.
        if (!aberto) setBusca("");
        onOpenChange(aberto);
      }}
    >
      <DialogContent className="gap-5 p-6 sm:max-w-3xl sm:p-8">
        <DialogHeader className="gap-2">
          <DialogTitle className="text-xl">{t("brandDialog.title")}</DialogTitle>
          <DialogDescription>
            {canSeeAllBrands ? t("brandDialog.allDescription") : t("brandDialog.ownDescription")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <BrandDialogSkeleton />
        ) : brands.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("brandDialog.empty")}</p>
        ) : (
          <>
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                aria-label={t("brandDialog.searchPlaceholder")}
                placeholder={t("brandDialog.searchPlaceholder")}
                autoComplete="off"
                className="h-11 pl-10 text-base sm:text-sm"
              />
            </div>
            {visibleBrands.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("brandDialog.noResults", { search: busca.trim() })}
              </p>
            ) : (
              <ScrollArea className="max-h-[65vh]">
                <div className="flex flex-col gap-2 pr-3">
                  {visibleBrands.map((brand) => {
                    const isActive = brand.id === activeId;
                    // Linha da marca desativada: não é botão de troca — entrar nela
                    // é 409 no back. O que sobra é reativar.
                    if (brand.deactivatedAt) {
                      return (
                        <div
                          key={brand.id}
                          className="flex items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground"
                        >
                          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                            <Building2 className="size-5" />
                          </span>
                          <span className="flex-1 truncate text-base line-through">
                            {brand.name}
                          </span>
                          {busyId === brand.id ? (
                            <Spinner className="size-4" />
                          ) : (
                            <>
                              <Badge variant="outline" className="text-muted-foreground">
                                {t("brandDialog.deleted")}
                              </Badge>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={busyId !== null}
                                onClick={() => void handleReactivate(brand)}
                              >
                                <RotateCcw className="size-3" />
                                {t("brandDialog.reactivate")}
                              </Button>
                              <Button
                                type="button"
                                size="icon-sm"
                                variant="ghost"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                title={t("brandDialog.purgeTitle")}
                                aria-label={t("brandDialog.purgeAria", { name: brand.name })}
                                disabled={busyId !== null}
                                onClick={() => {
                                  setConfirmacao("");
                                  setApagando(brand);
                                }}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      );
                    }
                    const queueTotal = queueCounts?.[brand.id];
                    return (
                      <Button
                        variant="ghost"
                        key={brand.id}
                        type="button"
                        disabled={busyId !== null}
                        onClick={() => void handleSelect(brand)}
                        aria-current={isActive ? "true" : undefined}
                        className={cn(
                          "h-auto whitespace-normal flex items-center gap-4 rounded-lg border px-4 py-3 text-left hover:bg-accent disabled:opacity-50",
                          isActive && "border-primary/40 bg-primary/5",
                        )}
                      >
                        <span
                          className={cn(
                            "flex size-10 shrink-0 items-center justify-center rounded-lg",
                            isActive
                              ? "bg-primary/10 text-primary"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          <Building2 className="size-5" />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="truncate text-base font-medium">{brand.name}</span>
                          {isActive ? (
                            <Badge variant="secondary" className="w-fit gap-1">
                              <Check className="size-3" /> {t("brandDialog.active")}
                            </Badge>
                          ) : !brand.isMember ? (
                            <Badge variant="outline" className="w-fit gap-1 text-muted-foreground">
                              <Plus className="size-3" /> {t("brandDialog.createAccess")}
                            </Badge>
                          ) : null}
                        </span>
                        {busyId === brand.id ? (
                          <Spinner className="size-5" />
                        ) : isLoadingQueueCounts ? (
                          <Skeleton className="h-9 w-20 rounded-md" />
                        ) : typeof queueTotal === "number" ? (
                          <span className="flex shrink-0 flex-col items-end leading-tight">
                            {queueTotal > 0 ? (
                              <>
                                <span className="text-xl font-semibold tabular-nums">
                                  {formatNumber(queueTotal)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {t("brandDialog.queueLeads", { count: queueTotal })}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {t("brandDialog.queueEmpty")}
                              </span>
                            )}
                          </span>
                        ) : null}
                      </Button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </>
        )}

        {canCreateBrand && !isLoading ? (
          <Button
            variant="ghost"
            type="button"
            disabled={busyId !== null}
            onClick={() => setCreating(true)}
            className="h-auto whitespace-normal flex items-center gap-4 rounded-lg border border-dashed px-4 py-3 text-left text-base text-muted-foreground hover:border-solid hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Plus className="size-5" />
            </span>
            <span className="flex-1 truncate">{t("brandDialog.create")}</span>
          </Button>
        ) : null}
        {canOfferFranchise && !isLoading && (
          <section
            className="rounded-xl border bg-muted/30 p-4"
            aria-labelledby="brand-franchise-title"
          >
            <div className="flex items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Network aria-hidden="true" className="size-4" />
              </span>
              <div className="min-w-0 space-y-2">
                <h3 id="brand-franchise-title" className="text-sm font-semibold">
                  {t("brandDialog.franchise.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("brandDialog.franchise.description")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busyId !== null}
                  nativeButton={false}
                  role="link"
                  render={<Link to="/support/tickets" />}
                  onClick={() => onOpenChange(false)}
                >
                  {t("brandDialog.franchise.action")}
                  <ArrowUpRight aria-hidden="true" className="size-4" />
                </Button>
              </div>
            </div>
          </section>
        )}
      </DialogContent>

      <BrandCreateDialog
        open={creating}
        onOpenChange={setCreating}
        busy={busyId !== null}
        onSubmit={handleCreate}
      />

      <AlertDialog
        open={apagando !== null}
        onOpenChange={(aberto) => {
          if (!aberto && busyId === null) {
            setApagando(null);
            setConfirmacao("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("brandDialog.confirmationTitle", { name: apagando?.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("brandDialog.confirmationBefore")} <strong>{t("brandDialog.everything")}</strong>
              {t("brandDialog.confirmationAfter")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="confirmar-nome-marca">
              {t("brandDialog.typeBefore")}{" "}
              <span className="font-medium text-foreground">{apagando?.name}</span>{" "}
              {t("brandDialog.typeAfter")}
            </Label>
            <Input
              id="confirmar-nome-marca"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="off"
              disabled={busyId !== null}
              placeholder={apagando?.name}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyId !== null}>
              {t("brandDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={!confirmado || busyId !== null}
              onClick={(e) => {
                // O Radix fecha no clique; aqui o fechamento é do handler, para
                // o dialog não sumir antes da resposta do back.
                e.preventDefault();
                if (apagando) void handlePurge(apagando);
              }}
            >
              {busyId !== null ? <Spinner className="size-4" /> : <Trash2 className="size-4" />}
              {t("brandDialog.purgeForever")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
